import { ChangeEvent, FC, Fragment, useCallback, useContext, useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import {
  Checkbox,
  Icon,
  Input,
  List,
  ListItem,
  Select,
  SelectCard,
  SelectCardCheckBox,
  Text,
  Textarea,
  Tooltip
} from '@/client/components'
import { CardPackageSimple } from '@/client/components/CardPackageSimple'
import { CartContext } from '@/client/context/cart'
import { useDebounce, useMutatePrices } from '@/client/hooks'
import { getTemplateFilteredOptions, handleSelectKeyValue } from '@/client/modules/OrderTemplates/orderTemplateUtils'
import { IField, IFieldOption } from '@/client/types'
import * as S from '../styles'
import { ARTWORKSERVICE_SELECTION, FieldType, OptionsChildProps } from '../types'

export const Options: FC<OptionsChildProps> = ({
  fields,
  texts,
  wpText,
  isArtworkService,
  artworkServiceText,
  initialSelection,
  template,
  compactView = false
}) => {
  const { locale } = useRouter()
  const { mutate } = useMutatePrices(locale)
  const { updateSelection, selection } = useContext(CartContext)
  const [checkRecipient, setCheckedRecipient] = useState<boolean>(false)
  const debouncedSelection = useDebounce(selection, 500)

  const cardText = (type: string) => wpText.packages.find((packages) => packages.name === type)

  const smartPackage = cardText('Smart')
  const proPackage = cardText('Pro')

  useEffect(() => {
    mutate(debouncedSelection)
  }, [debouncedSelection])

  const handleChange = useCallback(
    (optionId, value) => {
      let recipientValue
      if (optionId === 'recipient') {
        if (checkRecipient) {
          recipientValue = '1'
          setCheckedRecipient(false)
        } else {
          recipientValue = '0'
          setCheckedRecipient(true)
        }
      }

      updateSelection({
        ...selection,
        [optionId]: value,
        ['recipient']: recipientValue
      })
    },
    [selection, checkRecipient]
  )

  const handleSelect = useCallback(
    (optionId, value, relatedField, relatedFieldId) => {
      let newSelection
      if (selection.super_express_production == true && optionId === 'artwork_service') {
        // compare with == true as it does automatic conversion to boolean
        newSelection = { ...selection }
      } else {
        newSelection = {
          ...selection,
          [optionId]: value,
          [relatedField]: relatedFieldId
        }
      }

      updateSelection(newSelection)
      mutate(newSelection)
    },
    [selection, updateSelection, mutate]
  )

  const handleSelectCheckbox = useCallback(
    (optionId, nextFieldId, relatedField, relatedFieldId) => {
      let newSelection
      if (selection.super_express_production == true && optionId === 'no_artwork_service') {
        newSelection = {
          ...selection
        }
      } else {
        newSelection = {
          ...selection,
          [optionId]: nextFieldId,
          [relatedField]: relatedFieldId
        }
      }

      updateSelection(newSelection)
      mutate(newSelection)
    },
    [mutate, selection, updateSelection]
  )

  const handleChangeCheckbox = useCallback(
    (optionId, value) => {
      let newOptions = selection[optionId] as string[]
      const isChecked = newOptions.some((item: string) => item === value)

      if (isChecked) {
        newOptions = newOptions.filter((item: string) => item !== value)
      } else {
        newOptions.push(value)
      }

      const newSelection = {
        ...selection,
        [optionId]: newOptions
      }

      updateSelection(newSelection)
      mutate(newSelection)
    },
    [selection, updateSelection, mutate]
  )

  const isDisabled = useCallback(
    (disableList): boolean => {
      if (!disableList) return false
      return disableList.some(({ key, value }) => selection[key] === value)
    },
    [selection]
  )

  const handleAssemblyChange = useCallback(
    (optionId, value) => {
      const oppositeSelection = optionId === 'maximumRollDiameter' ? 'labelsNumberPerRoll' : 'maximumRollDiameter'

      const newSelection = {
        ...selection,
        [optionId]: value,
        [oppositeSelection]: '-'
      }

      updateSelection(newSelection)
      mutate(newSelection)
    },
    [updateSelection, selection, mutate]
  )

  const templateFilteredOptions = (field: IField, excludeDisabledOptions: boolean = false) =>
    getTemplateFilteredOptions(field, excludeDisabledOptions, template, isDisabled, undefined)

  const handleSelectLight = (fieldKey, value) => {
    handleSelectKeyValue(fieldKey, value, fields, handleSelect)
  }

  useEffect(() => {
    fields.forEach((field) => {
      if (!field.force_value) return
      const { id, key, value, nextFieldId, relatedFieldId, relatedField } = field.force_value
      const onChangeTypes: Record<FieldType, () => void> = {
        ['textarea']: () => handleChange(key, value),
        ['input-number']: () => handleAssemblyChange(id, value),
        ['range']: () => handleChange(id, value),
        ['select-radio']: () => handleSelect(field.key, value, relatedField, relatedFieldId),
        ['checkbox']: () => handleChangeCheckbox(field.key, value),
        ['select-radio-vertical']: () => {},
        ['select-card-checkbox']: () => handleSelectCheckbox(field.key, nextFieldId, relatedField, relatedFieldId)
      }
      onChangeTypes[field.type]()
    })
  }, [fields])

  // REL-776 related
  // In compact view, "artwork_service" is rendered as a native <select> whose available options
  // (templateFilteredOptions with excludeDisabledOptions=true) can be narrowed by a selection made
  // elsewhere in the calculator (e.g. choosing an "opaque white" material disables the "Smart"
  // artwork service option). When the previously selected option is removed from the list, the
  // <select> falls back visually to showing the first remaining option (e.g. "Pro"), but that is
  // only a DOM rendering artifact - no onChange fires, so `selection.artwork_service` is never
  // updated and the stale, no-longer-visible value (e.g. "Smart") keeps being sent to the price
  // API. Detect that mismatch and correct the selection (and re-trigger pricing) to match what is
  // actually shown.
  useEffect(() => {
    if (!compactView) return

    const artworkServiceField = fields.find((field) => field.key === 'artwork_service')
    if (!artworkServiceField) return

    const currentValue = selection[artworkServiceField.key]
    if (currentValue === undefined || currentValue === null) return

    const validOptions = templateFilteredOptions(artworkServiceField, true)
    const isCurrentValueStillValid = validOptions.some((option) => option.id === currentValue)

    if (!isCurrentValueStillValid && validOptions.length > 0) {
      handleSelectLight(artworkServiceField.key, validOptions[0].id)
    }
  }, [selection, fields, template])

  return (
    <>
      <>
        {!isArtworkService && (
          <>
            {fields.map((field) => (
              <div key={'artworkservice_hide'} hidden={template?.[field?.key]?.hidden}>
                {field.key !== 'order_details' && ( //order name has been moved to summary step
                  <S.Block key={field.key} className={`tour-${field.key}`}>
                    {!compactView && (
                      <Text size='lg' family='heading' weight='bold' margin='0 0 1.5rem'>
                        {field.key === 'artwork_service' ? '' : field.displayName}
                        {field?.hint && (
                          <S.Hint>
                            <Tooltip title={field.hint}>
                              <Icon name='info' />
                            </Tooltip>
                          </S.Hint>
                        )}
                      </Text>
                    )}

                    <div>
                      {field.type === 'textarea' && (
                        <S.Option direction='column'>
                          {field?.options?.map(({ id, name, key }: IFieldOption) => (
                            <Textarea
                              key={id}
                              id={id}
                              isBlock
                              name={key}
                              //hasError={!selection[key] || (selection[key] as string).trim().length === 0}
                              label={name}
                              value={selection[key] as string}
                              dimension='small'
                              onChange={(event: ChangeEvent<HTMLTextAreaElement>): void => {
                                handleChange(key, event?.currentTarget?.value)
                              }}
                              style={{ backgroundColor: '#EAEDF0', borderRadius: '10px' }}
                              className={field.key}
                              // Changelog
                              // OnBlur has been commented since it affects with focus and click event.
                              // handleBlur() event has been added to handleChange() method.
                              // onBlur={(event: ChangeEvent<HTMLTextAreaElement>): void => {
                              //   handleBlur(key, event?.currentTarget?.value)
                              // }}
                            />
                          ))}
                        </S.Option>
                      )}

                      {field.type === 'input-number' && field.key === 'assembly' && (
                        <S.Option isFull>
                          {field?.options?.map(({ id, name, unit, min, max }: IFieldOption, index: number) => (
                            <Fragment key={id}>
                              <Input
                                isBlock
                                dimension='large'
                                type='number'
                                id={id}
                                name={name}
                                min={min}
                                max={max}
                                label={`${name} (${unit})`}
                                value={selection[id] as string}
                                onChange={(event: ChangeEvent<HTMLInputElement>): void => {
                                  handleAssemblyChange(id, event?.currentTarget?.value)
                                }}
                              />

                              {index === 0 && <S.Divider>{texts.choose_one_set}</S.Divider>}
                            </Fragment>
                          ))}
                        </S.Option>
                      )}

                      {field.type === 'range' && (
                        <S.Option>
                          {field?.options?.map(({ id, name }: IFieldOption) => (
                            <Input
                              dimension='large'
                              key={id}
                              id={id}
                              name={name}
                              label={name}
                              value={selection[id] as string}
                              onChange={(event: ChangeEvent<HTMLInputElement>): void => {
                                handleChange(id, event?.currentTarget?.value)
                              }}
                            />
                          ))}
                        </S.Option>
                      )}

                      {field.type === 'select-radio' && field.key !== 'artwork_service' && (
                        <S.Option className='selected-radio-wrapper'>
                          {field?.options?.map(
                            ({ id, name, description, icon, relatedField, relatedFieldId }: IFieldOption) => (
                              <SelectCard
                                key={id}
                                id={id}
                                value={id}
                                name={field.key}
                                label={name}
                                description={description}
                                checked={id === selection[field.key]}
                                onChange={() => handleSelect(field.key, id, relatedField, relatedFieldId)}
                                iconName={icon}
                              />
                            )
                          )}
                        </S.Option>
                      )}

                      {/* no artwork service; in compact view all artwork-service options are in one drop down */}
                      {field.type === 'select-card-checkbox' && (
                        <>
                          {!compactView &&
                            field?.options?.map(
                              ({ id, name, description, nextFieldId, relatedField, relatedFieldId }: IFieldOption) => (
                                <SelectCardCheckBox
                                  key={id}
                                  id={id}
                                  value={id}
                                  name={field.key}
                                  label={name}
                                  description={description}
                                  checked={ARTWORKSERVICE_SELECTION.NO_ARTWORK_SERVICE === selection['artwork_service']} // No artwork service strange approach key === true -> Changed
                                  kind='circle'
                                  subText=''
                                  rightEndText=''
                                  boxType='withIcon'
                                  onChange={() =>
                                    handleSelectCheckbox(field.key, nextFieldId, relatedField, relatedFieldId)
                                  }
                                />
                              )
                            )}
                        </>
                      )}

                      {/** Adding additional WP content for artwork_service */}
                      {field.type === 'select-radio' && field.key === 'artwork_service' && (
                        <>
                          {/* REL-776: Simplified artwork service drop down */}
                          {compactView && (
                            <>
                              <div>
                                <Text
                                  size='lg'
                                  family='heading'
                                  weight='bold'
                                  margin='0 0 0.8rem'
                                  color={selection[field?.key] ? 'normal' : 'danger'}
                                >
                                  {field.displayName}
                                </Text>
                              </div>
                              <Select
                                id={'artwork_service_select'}
                                name={'artwork_service_select'}
                                options={templateFilteredOptions(field, true)}
                                value={selection[field.key]}
                                onChange={(e) => {
                                  // We need to find the other details like relatedField/relatedFieldId
                                  handleSelectLight(field.key, e.target.value)
                                }}
                                disabled={template?.[field.key]?.disabled}
                              />
                            </>
                          )}

                          {!compactView && (
                            <S.OptionAligner>
                              <S.Option direction='row' isFull>
                                {field?.options?.map(
                                  ({
                                    id,
                                    name,
                                    description,
                                    icon,
                                    key,
                                    lock,
                                    disabled,
                                    relatedField,
                                    relatedFieldId,
                                    priceVariant
                                  }: IFieldOption) => (
                                    <>
                                      {key !== 'no_artwork_service' && !isDisabled(disabled) && (
                                        <S.PackageList
                                          name={key}
                                          checked={id === selection[field.key]}
                                          role='button'
                                          tabIndex={0}
                                          onClick={() => handleSelect(field.key, id, relatedField, relatedFieldId)}
                                          onKeyPress={() => handleSelect(field.key, id, relatedField, relatedFieldId)}
                                        >
                                          <SelectCard
                                            key={id}
                                            id={id}
                                            value={id}
                                            name={field.key}
                                            label={name}
                                            description={description}
                                            disabled={
                                              template?.[field.key]?.disabled ||
                                              lock == 1 ||
                                              isDisabled(disabled) ||
                                              selection.super_express_production
                                            }
                                            checked={id === selection[field.key]}
                                            onChange={() => handleSelect(field.key, id, relatedField, relatedFieldId)}
                                            iconName={icon}
                                            priceVariant={priceVariant ? `(${priceVariant})` : ''}
                                          />
                                          {key === 'Smart' && smartPackage && (
                                            <CardPackageSimple
                                              key={smartPackage.name}
                                              kind={smartPackage.type}
                                              name={smartPackage.name}
                                              description={smartPackage.description}
                                              list={smartPackage.service_list}
                                              price={smartPackage.price}
                                              prefix={smartPackage.price_prefix}
                                              suffix={smartPackage.price_suffix}
                                              observation={smartPackage.production_time}
                                              link={smartPackage.link}
                                              buttonLabel={smartPackage.button_label}
                                              compactView={compactView}
                                            />
                                          )}

                                          {key !== 'Smart' && proPackage && (
                                            <CardPackageSimple
                                              key={proPackage.name}
                                              kind={proPackage.type}
                                              name={proPackage.name}
                                              description={proPackage.description}
                                              list={proPackage.service_list}
                                              price={proPackage.price}
                                              prefix={proPackage.price_prefix}
                                              suffix={proPackage.price_suffix}
                                              observation={proPackage.production_time}
                                              link={proPackage.link}
                                              buttonLabel={proPackage.button_label}
                                              compactView={compactView}
                                            />
                                          )}
                                        </S.PackageList>
                                      )}
                                    </>
                                  )
                                )}
                              </S.Option>
                              <>
                                {!compactView && (
                                  <S.CleanCard>
                                    <Text size='md' family='heading' weight='bold' margin='0 0 1.5rem'>
                                      {wpText?.about_packages?.title}
                                    </Text>
                                    <Text weight='medium' margin='0 0 1.5rem'>
                                      {wpText?.about_packages?.subtitle}
                                    </Text>

                                    <List>
                                      {wpText?.about_packages?.service_list.map(({ text }) => (
                                        <div key={text}>
                                          <ListItem className='list-icon'>
                                            <span dangerouslySetInnerHTML={{ __html: text }} />
                                          </ListItem>
                                        </div>
                                      ))}
                                    </List>
                                  </S.CleanCard>
                                )}
                              </>
                            </S.OptionAligner>
                          )}

                          {/* Adding comment field for pro and smart service */}
                          {(selection.artwork_service > ARTWORKSERVICE_SELECTION.NO_ARTWORK_SERVICE ||
                            (selection.artwork_service !== initialSelection.artwork_service && // Not sure what the meaning of this is.
                              selection.no_artwork_service !== initialSelection.no_artwork_service)) && (
                            <S.Option direction='column'>
                              <Textarea
                                key={'artwork_service_description'}
                                id={'artwork_service_description'}
                                isBlock
                                name={'artwork_service_description'}
                                label={artworkServiceText.artwork_service_options.notes.placeholder}
                                value={selection['artwork_service_description'] as string}
                                dimension={compactView ? 'small' : 'medium'}
                                onChange={(event: ChangeEvent<HTMLTextAreaElement>): void => {
                                  handleChange('artwork_service_description', event?.currentTarget?.value)
                                }}
                                style={{ backgroundColor: '#EAEDF0', borderRadius: '10px' }}
                                className={'artwork_service_description'}
                              />
                            </S.Option>
                          )}
                        </>
                      )}

                      {field.type === 'checkbox' && (
                        <S.Option direction='column'>
                          {field?.options?.map(({ id, name, description }: IFieldOption) => (
                            <Checkbox
                              key={id}
                              id={id}
                              value={id}
                              name={field.key}
                              label={name}
                              description={description}
                              checked={(selection[field.key] as number[]).includes(Number(id))}
                              kind='square'
                              onChange={() => handleChangeCheckbox(field.key, id)}
                            />
                          ))}
                        </S.Option>
                      )}
                    </div>

                    {field.error_message && (
                      <Text margin='0.8rem 0 0' color='danger' size='sm'>
                        {field.error_message}
                      </Text>
                    )}
                  </S.Block>
                )}
              </div>
            ))}
          </>
        )}
      </>
      <>
        {/* Options Step for Smart and Pro Artwork Service calculator */}
        {isArtworkService && (
          <>
            <S.Block>
              <Text size='lg' family='heading' weight='bold' margin='0 0 1.5rem'>
                {artworkServiceText.artwork_service_options.basic_info.title}
              </Text>
              <S.Option direction='column'>
                <Textarea
                  key={'artwork_service_project_name'}
                  id={'artwork_service_project_name'}
                  isBlock
                  name={'artwork_service_project_name'}
                  hasError={
                    !selection['artwork_service_project_name'] ||
                    (selection['artwork_service_project_name'] as string).trim().length === 0
                  }
                  label={artworkServiceText.artwork_service_options.basic_info.project_name}
                  value={selection['artwork_service_project_name'] as string}
                  dimension='small'
                  onChange={(event: ChangeEvent<HTMLTextAreaElement>): void => {
                    handleChange('artwork_service_project_name', event?.currentTarget?.value)
                  }}
                  style={{ backgroundColor: '#EAEDF0', borderRadius: '10px' }}
                  className={'artwork_service_project_name'}
                />
              </S.Option>
            </S.Block>
            <S.Block>
              <Text size='lg' family='heading' weight='bold' margin='0 0 1.5rem'>
                {artworkServiceText?.artwork_service_options.recipient.title}
              </Text>
              <Text size='sm' color='weak' margin='0 0 1.5rem'>
                {artworkServiceText.artwork_service_options.recipient.text}
              </Text>
              <S.Option direction='column'>
                <Checkbox
                  id='recipient'
                  name='recipient'
                  value={selection['recipient'] as string}
                  label={artworkServiceText.artwork_service_options.recipient.checkbox}
                  checked={selection['recipient'] !== '' && checkRecipient}
                  kind='circle'
                  onChange={(event) => {
                    handleChange('recipient', event?.currentTarget?.value)
                  }}
                />
                {checkRecipient && (
                  <S.ArtworkOptionsWrapper>
                    <Input
                      isBlock
                      dimension='small'
                      type='text'
                      id='first_name'
                      name='first_name'
                      placeholder={artworkServiceText.artwork_service_options.recipient.first_name}
                      value={selection['first_name'] as string}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        handleChange('first_name', event?.currentTarget?.value)
                      }}
                      //hasError={!selection['first_name'] || (selection['first_name'] as string).trim().length === 0}
                      className={'first_name'}
                    />
                    <Input
                      isBlock
                      dimension='small'
                      type='text'
                      id='surname'
                      name='surname'
                      placeholder={artworkServiceText.artwork_service_options.recipient.last_name}
                      value={selection['surname'] as string}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        handleChange('surname', event?.currentTarget?.value)
                      }}
                      //hasError={!selection['last_name'] || (selection['last_name'] as string).trim().length === 0}
                      className={'last_name'}
                    />

                    <Input
                      isBlock
                      dimension='small'
                      type='text'
                      id='email'
                      name='email'
                      placeholder={artworkServiceText.artwork_service_options.recipient.email}
                      value={selection['email'] as string}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        handleChange('email', event?.currentTarget?.value)
                      }}
                      //hasError={!selection['email'] || (selection['email'] as string).trim().length === 0}
                      className={'email'}
                    />
                  </S.ArtworkOptionsWrapper>
                )}
              </S.Option>
            </S.Block>
            <S.Block>
              <Text size='lg' family='heading' weight='bold' margin='0 0 1.5rem'>
                {artworkServiceText.artwork_service_options.notes.title}
              </Text>
              <S.Option direction='column'>
                <Textarea
                  key={'artwork_service_description'}
                  id={'artwork_service_description'}
                  isBlock
                  name={'artwork_service_description'}
                  hasError={
                    !selection['artwork_service_description'] ||
                    (selection['artwork_service_description'] as string).trim().length === 0
                  }
                  label={artworkServiceText.artwork_service_options.notes.placeholder}
                  value={selection['artwork_service_description'] as string}
                  dimension='medium'
                  onChange={(event: ChangeEvent<HTMLTextAreaElement>): void => {
                    handleChange('artwork_service_description', event?.currentTarget?.value)
                  }}
                  style={{ backgroundColor: '#EAEDF0', borderRadius: '10px' }}
                  className={'artwork_service_description'}
                />
              </S.Option>
            </S.Block>
          </>
        )}
      </>
    </>
  )
}
